import {
  AdminRole,
  AdminPermission,
  AdminDashboardStats,
  AdminClientItem,
  AdminCompanionItem,
  AdminBookingItem,
  AdminPaymentItem,
  AdminPayoutItem,
  FinanceLedgerEntry,
  AdminRefundItem,
  AdminDisputeItem,
  AdminAuditLogItem,
  AdminNotificationAlert,
} from '../types/admin.types';
import { apiFetch as fetch } from './apiConfig';
import { auth } from './firebase';

const ADMIN_TOKEN_KEY = 'pairup_admin_auth_token_v1';
const ADMIN_SESSION_DATA_KEY = 'pairup_admin_session_data_v1';
const ADMIN_MFA_SESSION_KEY = 'pairup_admin_mfa_session_id_v1';
const ADMIN_MFA_EXPIRY_KEY = 'pairup_admin_mfa_expiry_v1';

// In-memory fallback if sessionStorage is unavailable (e.g. Node test environment, SSR, private mode)
let memoryMfaSessionId: string | null = null;
let memoryMfaExpiryMs: number | null = null;

export function getAdminMfaSession(): { sessionId: string; expiresAtMs: number } | null {
  try {
    if (typeof sessionStorage !== 'undefined') {
      const sessionId = sessionStorage.getItem(ADMIN_MFA_SESSION_KEY);
      const rawExpiry = sessionStorage.getItem(ADMIN_MFA_EXPIRY_KEY);
      if (sessionId && rawExpiry) {
        const expiresAtMs = Number(rawExpiry);
        if (Number.isFinite(expiresAtMs) && expiresAtMs > Date.now()) {
          return { sessionId, expiresAtMs };
        }
        clearAdminMfaSession();
        return null;
      }
    }
  } catch {
    // sessionStorage not accessible
  }

  if (memoryMfaSessionId && memoryMfaExpiryMs && memoryMfaExpiryMs > Date.now()) {
    return { sessionId: memoryMfaSessionId, expiresAtMs: memoryMfaExpiryMs };
  }
  if (memoryMfaSessionId && memoryMfaExpiryMs && memoryMfaExpiryMs <= Date.now()) {
    clearAdminMfaSession();
  }
  return null;
}

export function setAdminMfaSession(sessionId: string, expiresAt: string | number | Date) {
  if (!sessionId) {
    clearAdminMfaSession();
    return;
  }
  const expiresAtMs =
    typeof expiresAt === 'number'
      ? expiresAt
      : new Date(expiresAt).getTime();

  if (!Number.isFinite(expiresAtMs) || expiresAtMs <= Date.now()) {
    clearAdminMfaSession();
    return;
  }

  memoryMfaSessionId = sessionId;
  memoryMfaExpiryMs = expiresAtMs;

  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(ADMIN_MFA_SESSION_KEY, sessionId);
      sessionStorage.setItem(ADMIN_MFA_EXPIRY_KEY, String(expiresAtMs));
    }
  } catch {
    // sessionStorage not accessible
  }
}

export function clearAdminMfaSession() {
  memoryMfaSessionId = null;
  memoryMfaExpiryMs = null;
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(ADMIN_MFA_SESSION_KEY);
      sessionStorage.removeItem(ADMIN_MFA_EXPIRY_KEY);
    }
  } catch {
    // ignore
  }
}

export function getAdminToken(): string | null {
  return localStorage.getItem(ADMIN_TOKEN_KEY) || localStorage.getItem('pairup_admin_token');
}

export function setAdminToken(token: string) {
  localStorage.setItem(ADMIN_TOKEN_KEY, token);
  localStorage.setItem('pairup_admin_token', token);
}

export function clearAdminToken() {
  localStorage.removeItem(ADMIN_TOKEN_KEY);
  localStorage.removeItem('pairup_admin_token');
  localStorage.removeItem(ADMIN_SESSION_DATA_KEY);
  clearAdminMfaSession();
}

export function getStoredAdminInfo(): any {
  try {
    const raw = localStorage.getItem(ADMIN_SESSION_DATA_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function setStoredAdminInfo(data: any) {
  localStorage.setItem(ADMIN_SESSION_DATA_KEY, JSON.stringify(data));
}

async function resolveFreshAdminToken(): Promise<string | null> {
  if (auth.currentUser) {
    try {
      const freshToken = await auth.currentUser.getIdToken();
      if (freshToken) {
        setAdminToken(freshToken);
        return freshToken;
      }
    } catch {
      // Fall back to stored token
    }
  }
  return getAdminToken();
}

async function adminFetch(endpoint: string, options: RequestInit = {}) {
  const token = await resolveFreshAdminToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
    headers['x-admin-token'] = token;
  }

  // Include x-admin-mfa-session with the active, unexpired verified MFA session ID
  const mfaSession = getAdminMfaSession();
  if (mfaSession?.sessionId) {
    headers['x-admin-mfa-session'] = mfaSession.sessionId;
  }

  const res = await fetch(endpoint, {
    ...options,
    headers,
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    // If backend returns 403 with ADMIN_MFA_REQUIRED or expired/revoked session, clear local MFA session
    if (
      res.status === 403 &&
      (data.code === 'ADMIN_MFA_REQUIRED' ||
        data.error?.code === 'ADMIN_MFA_REQUIRED' ||
        data.error === 'ADMIN_MFA_REQUIRED' ||
        String(data.error?.message || data.error || '').includes('2FA') ||
        String(data.error?.message || data.error || '').includes('Multi-factor') ||
        String(data.error?.message || data.error || '').includes('APITXT'))
    ) {
      clearAdminMfaSession();
    }

    const err: any = new Error(data.error?.message || data.error || 'Admin request failed');
    err.code = data.error?.code || data.code || `HTTP_${res.status}`;
    err.status = res.status;
    throw err;
  }

  return data;
}

// ---------------------------------------------------------------------------
// Authentication API
// ---------------------------------------------------------------------------
export async function adminDirectEmailLoginApi(
  email: string,
  pass: string
): Promise<{
  success: boolean;
  customToken: string;
  mfaRequired?: boolean;
  mfaVerified?: boolean;
  admin: any;
}> {
  const res = await fetch('/api/admin/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: email.trim(), password: pass }),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.success) {
    const err: any = new Error(data.error?.message || data.error || 'Admin login failed');
    err.code = data.error?.code || data.code || `HTTP_${res.status}`;
    err.status = res.status;
    throw err;
  }

  return data;
}

export async function adminLoginApi(
  email: string,
  password: string,
  mfaCode?: string,
  rememberSession = true
) {
  const res = await fetch('/api/admin/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, mfaCode, rememberSession }),
  });

  const data = await res.json();
  if (!res.ok || !data.success) {
    throw new Error(data.error?.message || data.error || 'Admin login failed');
  }

  if (data.token) {
    setAdminToken(data.token);
  }
  if (data.admin) {
    setStoredAdminInfo(data.admin);
  }

  return data;
}

export async function adminLogoutApi(): Promise<{ success: boolean; message?: string }> {
  const currentMfaSession = getAdminMfaSession();
  const sessionId = currentMfaSession?.sessionId || null;
  try {
    const res = await adminFetch('/api/admin/auth/logout', {
      method: 'POST',
      body: JSON.stringify({ sessionId }),
    });
    clearAdminMfaSession();
    clearAdminToken();
    return res;
  } catch (err: any) {
    clearAdminMfaSession();
    clearAdminToken();
    throw err;
  }
}

export async function fetchAdminMeApi() {
  const data = await adminFetch('/api/admin/auth/me');
  if (data.admin) {
    setStoredAdminInfo(data.admin);
  }
  return data;
}

export async function sendAdminMfaOtpApi(phone?: string) {
  return adminFetch('/api/admin/auth/mfa/send-otp', {
    method: 'POST',
    body: JSON.stringify({ phone }),
  });
}

export async function verifyAdminMfaOtpApi(otp: string, phone?: string, verificationId?: string) {
  const data = await adminFetch('/api/admin/auth/mfa/verify-otp', {
    method: 'POST',
    body: JSON.stringify({ otp, phone, verificationId }),
  });

  // Securely persist returned session ID and expiry in per-browser-session storage
  if (data?.sessionId && (data?.expiresAt || data?.mfaVerifiedUntilMs)) {
    setAdminMfaSession(data.sessionId, data.expiresAt || data.mfaVerifiedUntilMs);
  }

  if (data.admin) {
    setStoredAdminInfo(data.admin);
  }
  return data;
}

// ---------------------------------------------------------------------------
// Dashboard & Analytics
// ---------------------------------------------------------------------------
export async function fetchAdminDashboardStats(): Promise<AdminDashboardStats> {
  const res = await adminFetch('/api/admin/dashboard');
  return res.data;
}

// ---------------------------------------------------------------------------
// Clients Management
// ---------------------------------------------------------------------------
export async function fetchAdminClients(search?: string, status?: string): Promise<AdminClientItem[]> {
  const params = new URLSearchParams();
  if (search) params.append('search', search);
  if (status && status !== 'ALL') params.append('status', status);

  const res = await adminFetch(`/api/admin/clients?${params.toString()}`);
  return res.data?.items || [];
}

export async function suspendClientApi(clientId: string, reason: string) {
  return adminFetch(`/api/admin/clients/${clientId}/suspend`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export async function reactivateClientApi(clientId: string, reason = 'Account reactivated by administrator.') {
  return adminFetch(`/api/admin/clients/${clientId}/reactivate`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

// ---------------------------------------------------------------------------
// Companions Management
// ---------------------------------------------------------------------------
export async function fetchAdminCompanions(search?: string, status?: string): Promise<AdminCompanionItem[]> {
  const params = new URLSearchParams();
  if (search) params.append('search', search);
  if (status && status !== 'ALL') params.append('status', status);

  const res = await adminFetch(`/api/admin/companions?${params.toString()}`);
  return res.data?.items || [];
}

export async function suspendCompanionApi(companionId: string, reason: string) {
  return adminFetch(`/api/admin/companions/${companionId}/suspend`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export async function reactivateCompanionApi(companionId: string, reason = 'Companion account reactivated by administrator.') {
  return adminFetch(`/api/admin/companions/${companionId}/reactivate`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

// ---------------------------------------------------------------------------
// Bookings Management
// ---------------------------------------------------------------------------
export async function fetchAdminBookings(status?: string): Promise<AdminBookingItem[]> {
  const params = new URLSearchParams();
  if (status && status !== 'ALL') params.append('status', status);

  const res = await adminFetch(`/api/admin/bookings?${params.toString()}`);
  return res.data?.items || [];
}

export async function cancelAdminBookingApi(bookingId: string, reason: string) {
  return adminFetch(`/api/admin/bookings/${bookingId}/cancel`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

// ---------------------------------------------------------------------------
// Payments, Payouts & Finance Ledger
// ---------------------------------------------------------------------------
export async function fetchAdminPayments(): Promise<AdminPaymentItem[]> {
  const res = await adminFetch('/api/admin/payments');
  return res.payments || res.data?.items || [];
}

export async function fetchAdminPayouts(): Promise<AdminPayoutItem[]> {
  const res = await adminFetch('/api/admin/payouts');
  return res.data?.items || [];
}

export async function approveAdminPayoutApi(payoutId: string) {
  return adminFetch(`/api/admin/payouts/${payoutId}/approve`, {
    method: 'POST',
  });
}

export async function fetchFinanceLedger(): Promise<FinanceLedgerEntry[]> {
  const res = await adminFetch('/api/admin/finance/ledger');
  return res.data?.items || [];
}

export async function fetchAdminRefunds(): Promise<AdminRefundItem[]> {
  const res = await adminFetch('/api/admin/refunds');
  return res.data?.items || [];
}

export async function createAdminRefundApi(paymentId: string, amount: number, reason: string) {
  return adminFetch('/api/admin/refunds/create', {
    method: 'POST',
    body: JSON.stringify({ paymentId, amount, reason }),
  });
}

// ---------------------------------------------------------------------------
// Disputes, Audit Logs, Reports & Settings
// ---------------------------------------------------------------------------
export async function fetchAdminDisputes(): Promise<AdminDisputeItem[]> {
  const res = await adminFetch('/api/admin/disputes');
  return res.data?.items || [];
}

export async function resolveAdminDisputeApi(disputeId: string, resolution: string, internalNote?: string) {
  return adminFetch(`/api/admin/disputes/${disputeId}/resolve`, {
    method: 'POST',
    body: JSON.stringify({ resolution, internalNote }),
  });
}

export async function fetchAdminAuditLogsApi(action?: string): Promise<AdminAuditLogItem[]> {
  const params = new URLSearchParams();
  if (action && action !== 'ALL') params.append('action', action);

  const res = await adminFetch(`/api/admin/audit-logs?${params.toString()}`);
  return res.data?.items || [];
}

export async function fetchAdminReportsApi() {
  const res = await adminFetch('/api/admin/reports');
  return res.data;
}

export async function fetchAdminSettingsApi() {
  const res = await adminFetch('/api/admin/settings');
  return res.data;
}

export async function updateAdminSettingsApi(settings: any) {
  return adminFetch('/api/admin/settings', {
    method: 'PATCH',
    body: JSON.stringify(settings),
  });
}
